/**
 * Plafonnement de la liste de messages du chat.
 *
 * Le flux courant est borné pour ne pas laisser la liste croître sans fin sur un live à gros
 * volume. Depuis la fusion des onglets (WELYA-382), les questions en attente vivent dans ce même
 * flux alors qu'elles n'étaient pas plafonnées dans leur onglet dédié : une question ouverte plus
 * ancienne que le plafond doit rester visible, sinon le staff n'a plus aucune vue où la retrouver.
 * Même chose pour les messages épinglés, qui sont les plus anciens de la liste côté spectateur
 * puisqu'ils arrivent par l'historique au chargement.
 */

export const CHAT_MESSAGES_LIST_MAX = 300;

/**
 * Question encore ouverte. La détection est pilotée par le back, qui repasse `question` à false
 * en même temps qu'il pose `questionHandled` lors de la résolution.
 */
export function isPendingQuestionMessageData(data: unknown): boolean {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return false;
  const d = data as { question?: unknown; questionHandled?: unknown };
  return d.question === true && d.questionHandled !== true;
}

/**
 * Prédicat de survie au plafond : question encore ouverte (vues staff) ou message épinglé.
 * Renvoie `undefined` quand il n'y a rien à protéger, pour laisser `capChatMessages` sur son
 * chemin le plus court.
 */
export function buildChatCapKeepAll<T extends { data?: unknown }>(options: {
  keepPendingQuestions: boolean;
  pinnedIds?: ReadonlySet<string>;
  getMessageId: (message: T) => string | number | null | undefined;
}): ((message: T) => boolean) | undefined {
  const { keepPendingQuestions, pinnedIds, getMessageId } = options;
  const keepPinned = pinnedIds != null && pinnedIds.size > 0;
  if (!keepPendingQuestions && !keepPinned) return undefined;

  return (message) => {
    if (keepPendingQuestions && isPendingQuestionMessageData(message.data)) return true;
    if (!keepPinned) return false;
    const messageId = getMessageId(message);
    return messageId != null && pinnedIds.has(messageId.toString());
  };
}

/**
 * Garde les `max` derniers messages, plus ceux que `keepAll` protège même s'ils sont antérieurs.
 * L'entrée doit déjà être triée chronologiquement : l'ordre est conservé tel quel.
 */
export function capChatMessages<T>(
  messages: T[],
  options?: { max?: number; keepAll?: (message: T) => boolean },
): T[] {
  const max = options?.max ?? CHAT_MESSAGES_LIST_MAX;
  if (messages.length <= max) return messages;

  const cutoff = messages.length - max;
  const recent = messages.slice(cutoff);
  const keepAll = options?.keepAll;
  if (!keepAll) return recent;

  const protectedOlder = messages.slice(0, cutoff).filter(keepAll);
  return protectedOlder.length > 0 ? [...protectedOlder, ...recent] : recent;
}
